import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import {
  Grid,
  Briefcase,
  Users,
  MessageSquare,
  Monitor,
  PenTool,
  Shield,
  Tag,
  Package,
  Globe,
  Link as LinkIcon,
  CheckSquare,
  ShoppingBag,
  TrendingUp,
  BarChart2,
  Settings,
  Mail,
  Phone,
  Calendar,
  Award,
  Folder,
  FileText,
  Star,
  Zap,
  Box,
  Sliders,
  Cpu,
  Layers,
} from 'react-feather';
import { useSelector } from 'react-redux';
import axiosService from '@Lib/axiosService';
import { toastError } from '@Utils/toastrHelper';
import useCustomQuery from '@Utils/useCustomQuery';
import { getAppSwitcherSettings } from '@Apis/settings';
import { DEFAULT_APP_SWITCHER_ITEMS, resolveAppUrl } from '@Lib/siteBranding';

const ICON_MAP = {
  Briefcase,
  Users,
  MessageSquare,
  Monitor,
  PenTool,
  Shield,
  Tag,
  Package,
  Globe,
  Link: LinkIcon,
  CheckSquare,
  Grid,
  ShoppingBag,
  TrendingUp,
  BarChart2,
  Settings,
  Mail,
  Phone,
  Calendar,
  Award,
  Folder,
  FileText,
  Star,
  Zap,
  Box,
  Sliders,
  Cpu,
  Layers,
};


export function renderAppIcon(iconName, size = 24) {
  const IconComponent = ICON_MAP[iconName] || Briefcase;
  return <IconComponent size={size} />;
}

function getItemStyles(item) {
  if (!item.theme_color && !item.bg_color) return { itemStyle: {}, iconStyle: {}, titleStyle: {} };

  const themeColor = item.theme_color;
  const bgColor = item.bg_color || (themeColor ? `${themeColor}18` : undefined);

  return {
    itemStyle: bgColor ? { backgroundColor: bgColor } : {},
    iconStyle: themeColor ? { backgroundColor: themeColor, color: '#ffffff' } : {},
    titleStyle: themeColor ? { color: themeColor } : {},
  };
}


function AppSwitcher({ mode = 'dropdown', setMenuVisibility }) {
  const userInfo = useSelector((state) => state.user.info);
  const projectActive = useSelector((state) => state.user.projectActive);

  const { data: remoteItems } = useCustomQuery(getAppSwitcherSettings, {}, true);

  const appItems = useMemo(() => {
    if (Array.isArray(remoteItems) && remoteItems.length > 0) {
      return remoteItems;
    }
    return DEFAULT_APP_SWITCHER_ITEMS;
  }, [remoteItems]);

  const isSale = useMemo(() => {
    if (!userInfo) return false;
    if (userInfo.is_super_admin) return true;
    const projectRoles = userInfo.roles_by_project?.[projectActive?.id] || [];
    return projectRoles.some((role) => String(role).toLowerCase().includes('sale'));
  }, [userInfo, projectActive]);

  const userRolesList = useMemo(() => {
    if (!userInfo) return [];
    const projectRoles = userInfo.roles_by_project?.[projectActive?.id] || [];
    const allRoles = Object.values(userInfo.roles_by_project || {}).flat();
    return [...projectRoles, ...allRoles].map((r) => String(r).toLowerCase());
  }, [userInfo, projectActive]);

  const visibleItems = useMemo(() => {
    return appItems
      .filter((item) => item.is_active !== false)
      .filter((item) => {
        if (!userInfo) return false;
        if (userInfo.is_super_admin) return true;

        const allowed =
          Array.isArray(item.allowed_roles) && item.allowed_roles.length > 0
            ? item.allowed_roles
            : item.permission === 'sale_only'
            ? ['sale']
            : ['all'];

        if (allowed.includes('all')) return true;

        return allowed.some((allowedRole) => {
          const target = String(allowedRole).toLowerCase();
          return userRolesList.some((userRole) => userRole.includes(target));
        });
      })
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [appItems, userInfo, userRolesList]);


  const handleOpenZaloOmni = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      const response = await axiosService.get(`${process.env.URL}/sso/zalo-omni-token`);
      const { redirect_url } = response.data;
      window.open(redirect_url, '_blank');
    } catch (error) {
      console.error('Không thể mở Zalo Omni:', error);
      toastError('Không thể kết nối Zalo Omni. Vui lòng thử lại.');
    }
  };

  const handleItemClick = (e, item) => {
    if (item.token_type === 'zalo_omni') {
      e.preventDefault();
      handleOpenZaloOmni(e);
      if (setMenuVisibility && window.innerWidth < 768) {
        setMenuVisibility();
      }
      return;
    }

    if (setMenuVisibility && window.innerWidth < 768) {
      setMenuVisibility();
    }
  };

  if (mode === 'dropdown') {
    return (
      <UncontrolledDropdown className="d-none d-md-block mr-2">
        <DropdownToggle tag="div" className="cursor-pointer">
          <Grid size={26} color="#222222ff" strokeWidth={1.8} />
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-apps">
          <div className="dropdown-header">Chuyển ứng dụng</div>
          {visibleItems.map((item) => {
            const finalUrl = resolveAppUrl(item.url, item.token_type, item.redirect_path);
            const themeClass = item.theme || 'theme-erp';
            const { itemStyle, iconStyle, titleStyle } = getItemStyles(item);

            if (item.token_type === 'zalo_omni') {
              return (
                <button
                  type="button"
                  key={item.id || item.name}
                  onClick={(e) => handleItemClick(e, item)}
                  className={`dropdown-item app-item ${themeClass}`}
                  style={itemStyle}
                >
                  <div className="app-icon-wrapper" style={iconStyle}>
                    {renderAppIcon(item.icon, 24)}
                  </div>
                  <div className="app-info">
                    <span className="app-name" style={titleStyle}>
                      {item.name}
                    </span>
                    {item.description && <span className="app-desc">{item.description}</span>}
                  </div>
                </button>
              );
            }

            return (
              <a
                key={item.id || item.name}
                href={finalUrl}
                target={item.target || '_self'}
                rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                onClick={(e) => handleItemClick(e, item)}
                className={`dropdown-item app-item ${themeClass}`}
                style={itemStyle}
              >
                <div className="app-icon-wrapper" style={iconStyle}>
                  {renderAppIcon(item.icon, 24)}
                </div>
                <div className="app-info">
                  <span className="app-name" style={titleStyle}>
                    {item.name}
                  </span>
                  {item.description && <span className="app-desc">{item.description}</span>}
                </div>
              </a>
            );
          })}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }

  // mode === 'list' (Mobile Drawer Menu)
  return (
    <div className="mobile-apps-switcher">
      <div className="apps-header d-flex align-items-center">
        <Grid size={16} className="mr-50" />
        <span>Chuyển ứng dụng</span>
      </div>
      <div className="apps-list d-flex flex-wrap">
        {visibleItems.map((item) => {
          const finalUrl = resolveAppUrl(item.url, item.token_type, item.redirect_path);
          const themeClass = (item.theme || 'theme-erp').replace('theme-', '');
          const { iconStyle, titleStyle } = getItemStyles(item);

          if (item.token_type === 'zalo_omni') {
            return (
              <a
                key={item.id || item.name}
                href="#"
                onClick={(e) => handleItemClick(e, item)}
                className={`app-link ${themeClass}`}
              >
                <div className="app-icon shadow-sm" style={iconStyle}>
                  {renderAppIcon(item.icon, 20)}
                </div>
                <span style={titleStyle}>{item.name}</span>
              </a>
            );
          }

          return (
            <a
              key={item.id || item.name}
              href={finalUrl}
              target={item.target || '_self'}
              rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
              onClick={(e) => handleItemClick(e, item)}
              className={`app-link ${themeClass}`}
            >
              <div className="app-icon shadow-sm" style={iconStyle}>
                {renderAppIcon(item.icon, 20)}
              </div>
              <span style={titleStyle}>{item.name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}


AppSwitcher.propTypes = {
  mode: PropTypes.oneOf(['dropdown', 'list']),
  setMenuVisibility: PropTypes.func,
};

export default AppSwitcher;
